import type { RgbaImage } from '../image';
import type { Rgb } from '../color';
import { deltaE, kmeans, oklabToSrgb, srgbToOklab } from './oklab';
import { MAX_INKS, newInkId, type Ink, type SepMode } from './model';

export function sampleOpaque(img: RgbaImage, maxDim = 1024) {
  const step = Math.max(1, Math.ceil(Math.max(img.width, img.height) / maxDim));
  const labs: number[] = [], ws: number[] = [];
  for (let y = 0; y < img.height; y += step) for (let x = 0; x < img.width; x += step) {
    const q = (y * img.width + x) * 4, a = img.data[q + 3];
    if (a < 128) continue;
    const [L, A, B] = srgbToOklab(img.data[q], img.data[q + 1], img.data[q + 2]);
    labs.push(L, A, B); ws.push(a / 255);
  }
  return { lab: Float32Array.from(labs), weights: Float32Array.from(ws), count: ws.length };
}

/**
 * OKLab hue anchors (degrees): red 29, orange 65, yellow 105, lime 125, green 142, teal 170, cyan 195, azure 235,
 * blue 264, violet 290, magenta 328, rose 350. Yellow carries a raised "Light" cutoff (0.95 vs the default 0.8):
 * saturated yellow is inherently very light in OKLab (e.g. [250,240,30] measures L≈0.9333, hue≈106.5°, nearest
 * anchor Yellow@105°), so the default 0.8 cutoff would mislabel ordinary yellows as "Light Yellow". Lime
 * carries the same cutoff for the same reason ([200,255,0] measures L≈0.9281, hue≈124°, anchor Lime@125°).
 */
const HUE_ANCHORS: ReadonlyArray<[string, number, number?]> = [['Red', 29], ['Orange', 65], ['Yellow', 105, 0.95], ['Lime', 125, 0.95], ['Green', 142], ['Teal', 170], ['Cyan', 195], ['Azure', 235], ['Blue', 264], ['Violet', 290], ['Magenta', 328], ['Rose', 350]];
export function nameForColor(rgb: Rgb): string {
  const [L, a, b] = srgbToOklab(rgb[0], rgb[1], rgb[2]);
  const chroma = Math.hypot(a, b);
  if (chroma < 0.03) return L < 0.2 ? 'Black' : L > 0.9 ? 'White' : 'Grey';
  let h = (Math.atan2(b, a) * 180) / Math.PI; if (h < 0) h += 360;
  let hue = 'Red', best = 999, lightCutoff = 0.8;
  for (const [name, deg, lt] of HUE_ANCHORS) { const d = Math.min(Math.abs(h - deg), 360 - Math.abs(h - deg)); if (d < best) { best = d; hue = name; lightCutoff = lt ?? 0.8; } }
  return L < 0.35 ? `Dark ${hue}` : L > lightCutoff ? `Light ${hue}` : hue;
}

function mkInk(rgb: Rgb, order: number, mode: SepMode, idPrefix?: string): Ink {
  return { id: newInkId(idPrefix), name: nameForColor(rgb), rgb, enabled: true, order, tolerance: mode === 'spot' ? 0.08 : 0.25, softness: 0.12, curve: [1, 1, 1] };
}

/** `idPrefix` namespaces the generated ink ids; pass a different one per thread (see `newInkId`). */
export function detectInks(img: RgbaImage, count: number, opts: { mode?: SepMode; mergeBelow?: number; seed?: number; idPrefix?: string } = {}): Ink[] {
  const mode = opts.mode ?? 'sim', mergeBelow = opts.mergeBelow ?? 0.05;
  const s = sampleOpaque(img);
  if (s.count === 0) return [];
  let k = count;
  if (k <= 0) {                                        // spot auto: count distinct colours (ΔE > 0.03), capped
    const distinct: number[][] = [];
    for (let i = 0; i < s.count; i++) { const c = [s.lab[i * 3], s.lab[i * 3 + 1], s.lab[i * 3 + 2]]; if (!distinct.some((d) => deltaE(d, c) <= 0.03)) { distinct.push(c); if (distinct.length > 64) break; } }
    k = Math.min(MAX_INKS, distinct.length);
  }
  k = Math.min(MAX_INKS, Math.max(1, k));
  const r = kmeans(s.lab, s.weights, k, { seed: opts.seed ?? 1 });
  // merge near-identical centroids, coverage-weighted
  const cents: { lab: number[]; w: number }[] = [];
  for (let j = 0; j < r.centroids.length / 3; j++) {
    const lab = Array.from(r.centroids.subarray(j * 3, j * 3 + 3)), w = r.counts[j];
    if (w === 0) continue;
    const near = cents.find((c) => deltaE(c.lab, lab) < mergeBelow);
    if (near) { const t = near.w + w; near.lab = near.lab.map((v, i) => (v * near.w + lab[i] * w) / t); near.w = t; } else cents.push({ lab, w });
  }
  cents.sort((a, b) => b.lab[0] - a.lab[0]);          // light → dark
  return cents.map((c, i) => mkInk(oklabToSrgb(c.lab[0], c.lab[1], c.lab[2]), i, mode, opts.idPrefix));
}

export function sortForPress(inks: Ink[]): Ink[] {
  return [...inks].sort((x, y) => srgbToOklab(...y.rgb)[0] - srgbToOklab(...x.rgb)[0]).map((ink, i) => ({ ...ink, order: i }));
}

/** Average the named inks in OKLab, coverage-weighted. The survivor is the first id in `ids`, so the
 *  caller decides which ink's name, curve and tolerance the merge keeps. */
export function mergeInks(inks: Ink[], ids: string[], coverage: Record<string, number>): Ink[] {
  const seen = new Set<string>(), group: Ink[] = [];
  for (const id of ids) { if (seen.has(id)) continue; seen.add(id); const ink = inks.find((i) => i.id === id); if (ink) group.push(ink); }
  if (group.length < 2) return inks;
  let L = 0, A = 0, B = 0, W = 0;
  for (const ink of group) { const w = (coverage[ink.id] ?? 0) + 1e-6; const [l, a, b] = srgbToOklab(...ink.rgb); L += l * w; A += a * w; B += b * w; W += w; }
  const merged: Ink = { ...group[0], rgb: oklabToSrgb(L / W, A / W, B / W) };
  return inks.filter((i) => !ids.includes(i.id) || i.id === group[0].id).map((i) => (i.id === group[0].id ? merged : i)).map((ink, k) => ({ ...ink, order: k }));
}
